import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import {
  CurrentTimeIndicator,
  CurrentTimeIndicatorInner,
} from "src/views/schedule/components/TimeIndicator";
import { BLOCK_WIDTH, SMALL_COLUMN_WIDTH } from "src/views/schedule/constants";

import { TMentorShiftScheduleEvent } from "../constants";

import { ShiftsColumn } from "./ShiftsColumn";
import { ShiftTimeLineColumn } from "./ShiftTimeLineColumn";

interface CalendarProps {
  daysToShow: string[];
  calendarShifts: TMentorShiftScheduleEvent[];
  isAdmin?: boolean;
  handleRegister?: (shiftId: number, index: number, spotsLeft: number) => void;
  handleUnregister?: (
    shiftId: number,
    index: number,
    spotsLeft: number
  ) => void;
  setSelectedShift: (shiftId: number) => void;
}

export const Calendar: React.FC<CalendarProps> = ({
  daysToShow,
  calendarShifts,
  isAdmin = false,
  handleRegister,
  handleUnregister,
  setSelectedShift,
}) => {
  // Scroll to current time
  const scrollRef = useRef<HTMLDivElement>(null);
  const executeScroll = () => scrollRef.current?.scrollIntoView();
  useEffect(() => {
    executeScroll();
  }, []);

  const getTimePosition = () =>
    moment().diff(moment("2026-09-18"), "days") * 24 +
    moment().hour() +
    moment().minute() / 60;

  const [timePosition, setTimePosition] = useState(getTimePosition());
  const [currentDay, setCurrentDay] = useState(moment().tz("America/New_York")); // Change time to "2021-01-13T10:46:05+0000" for testing

  const timeSetter = useCallback(() => {
    setTimePosition(getTimePosition());
    setCurrentDay(
      moment(moment.now()).subtract(50, "days").subtract(12, "hours")
    );
  }, []);

  useEffect(() => {
    const id = setInterval(timeSetter, 60000);
    return () => clearInterval(id);
  }, [timeSetter]);

  const maxColumnWidth = useMemo(() => {
    let maxOverlapColumn = 0;
    calendarShifts.forEach((day) => {
      maxOverlapColumn = Math.max(day.overlapColumn, maxOverlapColumn);
    });
    return (maxOverlapColumn + 1) * BLOCK_WIDTH;
  }, [calendarShifts]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const containerRef = scrollContainerRef.current;

    if (!containerRef) return;

    const handleScroll = () => {
      ReactTooltip.hide();
    };

    containerRef.addEventListener("scroll", handleScroll);

    return () => {
      containerRef.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Container>
      <SubContainer ref={scrollContainerRef}>
        <ScrollContainer>
          <TimeLineColumnsContainer>
            {daysToShow.map((day) => (
              <ShiftTimeLineColumn key={day} date={moment(day).toDate()} />
            ))}
          </TimeLineColumnsContainer>
          <ShiftsColumnsContainer>
            <CurrentTimeIndicator
              blocksFromTop={timePosition}
              ref={scrollRef}
            />
            {daysToShow.map((day, i) => (
              <>
                {currentDay.isSame(day) && (
                  <CurrentTimeIndicatorInner
                    inDayView={false}
                    blocksFromTop={timePosition}
                    blocksFromLeft={i}
                    totalBlocks={daysToShow.length}
                  />
                )}
                {/* only one shift column populate shifts are placed absolute and in a column (handles overnight shifts)*/}
                <ShiftsColumn
                  key={day}
                  shifts={i === 0 && calendarShifts ? calendarShifts : []}
                  isAdmin={isAdmin}
                  handleRegister={handleRegister}
                  handleUnregister={handleUnregister}
                  setSelectedShift={setSelectedShift}
                  columnWidth={maxColumnWidth}
                />
              </>
            ))}
          </ShiftsColumnsContainer>
        </ScrollContainer>
      </SubContainer>
    </Container>
  );
};

const Container = styled.div`
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid #c3ccd7;
  border-radius: 7px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: 45px;
  height: 100%;
`;

const ScrollContainer = styled.div`
  padding-top: 4rem;
  display: flex;
  width: fit-content;
  min-width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
`;

const TimeLineColumnsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ShiftsColumnsContainer = styled.div`
  width: max-content;
  min-width: calc(100% - ${SMALL_COLUMN_WIDTH}px);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SubContainer = styled.div`
  display: flex;
  flex-grow: 1;
  position: relative;
  overflow-x: auto;
  overflow-y: scroll;
`;

export const BannerEvent = styled.div`
  border: 2px solid ${({ theme }) => theme.globalConstants.color.brandPrimary};
  box-sizing: border-box;
  border-radius: 9px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
  display: flex;
  justify-content: center;
  padding: 4px;
  margin-bottom: 8px;
  width: 100%;
`;
